/**
 * monthlyActualCostHierarchy.js
 * ─────────────────────────────────────────────────────────────
 * PRESENTATION MODEL ONLY — not a financial engine.
 *
 * Architecture contract (agreed before implementation, Phase 0 + addenda):
 *
 *   - This module owns exactly two responsibilities:
 *       1. Building lookup structures from raw reference data
 *          (cost_codes, resource_dictionary) — internal, not exported.
 *       2. Projecting an already-computed `rows` dataset into a
 *          Resource Type → Cost Code → Sub Code presentation tree —
 *          exported as buildHierarchyFromRows().
 *   - It has NO knowledge of supabase, no network access, and performs
 *     no financial *business* calculation. The only operations are
 *     grouping and summation (Σ) of amounts already produced elsewhere
 *     (buildMonthlyActualCostData's agg/addAmt step). Aggregation is
 *     allowed because this is a projection of an existing result, not a
 *     new computation.
 *   - Movement reuse, not recompute: every movement figure uses the
 *     exact same formula already used for byCostCode/bySubCode/
 *     byResourceCode in monthlyActualCost.js —
 *     periodTotals[lastPeriod] - periodTotals[prevPeriod] — applied at
 *     each hierarchy level. No alternate formula is ever introduced.
 *   - Stateless preference rule: prefer stateless code over cached code
 *     unless profiling proves a cache is needed. Lookups are rebuilt
 *     from raw reference data on every call — an O(n) pass over
 *     cost_codes/resource_dictionary, negligible next to report
 *     generation itself. No caching, no hidden state, no memoization.
 *   - Deterministic builder: given the same rows/periods/lastPeriod/
 *     prevPeriod/lookups inputs, buildHierarchyFromRows() always
 *     produces the exact same output.
 *
 *   Single Builder Invariant (must always hold — architectural, not a
 *   runtime-checkable property; enforced by code review, not assertion):
 *     For every period P:
 *       hierarchyByPeriod[P] === buildHierarchyFromRows(rows filtered
 *       for P, [P], P, null, lookups)
 *     For the full dataset:
 *       hierarchy === buildHierarchyFromRows(rows, periods, lastPeriod,
 *       prevPeriod, lookups)
 *     No special rules. No alternative aggregation logic. No
 *     period-specific calculations. Every hierarchy in the system is
 *     just another projection of the same dataset, through this same
 *     function. buildHierarchyFromRows() is the ONLY function in this
 *     codebase that builds a hierarchy tree — there is no second path.
 *
 *   Reconciliation invariants (must always hold):
 *       Σ(subCodes under a Cost Code)       = that Cost Code's total
 *       Σ(Cost Codes under a Resource Type) = that Resource Type's total
 *       Σ(Resource Types)                   = grandTotal (for the same
 *                                              rows/periods scope)
 *
 *   Removability: deleting this module and its call sites in
 *   monthlyActualCost.js must leave rows/grandTotals/grandTotal/
 *   cumulativeTotals/grandMovement/byCostCode/bySubCode/byResourceCode
 *   completely unaffected.
 */

// ── internal — not exported ──────────────────────────────────────────
// Builds code -> row / id -> row / dictCode -> resourceType lookup maps
// from raw reference data. Pure, stateless, rebuilt on every call by
// design (see Stateless preference rule above).
//
// indirectCategoryMap (id -> name) and the cost_type / indirect_category_id
// fields already present on each ccData row (read directly off codeToRow
// entries, not a separate map) are what resolveIndirectCategory() below
// needs — the SAME reference data buildCostControlData.js's
// buildCumulativeData() reads for Cost Control's own Direct/Indirect
// split and category grouping. No parallel classification is introduced.
function buildLookups(ccData, dictData, indirectCategoriesData) {
  const codeToRow = {}
  ;(ccData || []).forEach(c => { if (c.code) codeToRow[c.code.trim().toLowerCase()] = c })
  const idToRow = {}
  ;(ccData || []).forEach(c => { idToRow[c.id] = c })
  const dictTypeMap = {}
  ;(dictData || []).forEach(d => { if (d.code) dictTypeMap[d.code.trim().toUpperCase()] = d.resource_type })
  const indirectCategoryMap = {}
  ;(indirectCategoriesData || []).forEach(cat => { if (cat.id) indirectCategoryMap[cat.id] = cat.name })
  return { codeToRow, idToRow, dictTypeMap, indirectCategoryMap }
}

// ── internal — not exported ──────────────────────────────────────────
// Same traversal algorithm as monthlyActualCost.js's own
// resolveToCostCode(): walk up from a Sub Code / Resource Code to its
// top-level (level 1) Cost Code. Kept as a separate implementation
// (rather than imported from monthlyActualCost.js) to avoid a circular
// dependency between the data layer and this presentation module — the
// data layer imports FROM this module, never the other way around.
function resolveToCostCode(code, { codeToRow, idToRow }) {
  if (!code) return code
  let row = codeToRow[code.trim().toLowerCase()]
  if (!row) return code
  let hops = 0
  while (row.level !== 1 && row.parent_id && hops < 5) {
    const parent = idToRow[row.parent_id]
    if (!parent) break
    row = parent
    hops++
  }
  return row.code
}

// ── internal — not exported ──────────────────────────────────────────
// Same resolution rule used to build byCostCode in monthlyActualCost.js:
// IPC / Petty Cash rows key off their Sub Code (since those sources post
// against a resource-level code, not a real top-level Cost Code); every
// other source keys off its own costCode field.
function realCodeFor(row, lookups) {
  return resolveToCostCode((row.source === 'IPC' || row.source === 'Petty Cash') ? row.subCode : row.costCode, lookups)
}

// ── internal — not exported ──────────────────────────────────────────
// Walk up from a Sub Code toward the top-level Cost Code, one level at a
// time, stopping at the first ancestor (or the code itself) that has a
// resource_dict_code with a known resource_type. Returns 'Unclassified'
// if no ancestor in the chain resolves to a known type — never throws,
// never drops the row from the hierarchy.
export function resolveResourceType(code, { codeToRow, idToRow, dictTypeMap }) {
  const UNCLASSIFIED = 'Unclassified'
  if (!code) return UNCLASSIFIED
  let row = codeToRow[code.trim().toLowerCase()]
  if (!row) return UNCLASSIFIED
  let hops = 0
  while (row && hops < 6) {
    const dictCode = row.resource_dict_code || row.code
    const type = dictCode ? dictTypeMap[dictCode.trim().toUpperCase()] : null
    if (type) return type
    if (!row.parent_id) break
    row = idToRow[row.parent_id]
    hops++
  }
  return UNCLASSIFIED
}

// Same traversal as resolveResourceType() above, but returns the
// resource_dict_code itself (e.g. "RC-001") instead of the resource_type
// it maps to (e.g. "Material"). This is the classifier for the "By
// Resource Code" grouping mode — a finer-grained view than "By Source
// Type" (Resource Type), using the Resource Dictionary code directly.
// Returns 'Unclassified' if no ancestor in the chain has a
// resource_dict_code at all — never throws, never drops the row.
export function resolveResourceDictCode(code, { codeToRow, idToRow }) {
  const UNCLASSIFIED = 'Unclassified'
  if (!code) return UNCLASSIFIED
  let row = codeToRow[code.trim().toLowerCase()]
  if (!row) return UNCLASSIFIED
  let hops = 0
  while (row && hops < 6) {
    if (row.resource_dict_code) return row.resource_dict_code
    if (!row.parent_id) break
    row = idToRow[row.parent_id]
    hops++
  }
  return UNCLASSIFIED
}

// ── Indirect classification — reuses Cost Control's exact categorization,
// never a parallel one. See buildCostControlData.js's buildCumulativeData():
// Direct/Indirect is decided by cost_codes.cost_type ('direct'/'indirect'),
// and Indirect Cost Codes are grouped by cost_codes.indirect_category_id
// (a Level-1, Cost-Code-level field — indirect_category_id is NOT read
// from resource_dictionary and has no Resource Code level at all, unlike
// Direct's Material/Labour/Subcontract/Equipment split). Both maps below
// read the exact same reference columns (ccData's cost_type /
// indirect_category_id, and indirect_categories' id -> name) that Cost
// Control itself reads — adding, renaming, or reassigning a category on
// the Cost Codes page is reflected here automatically on next load, with
// zero code changes here.

// ── exported ──────────────────────────────────────────────────────────
// True if the row's resolved top-level Cost Code is Indirect
// (cost_codes.cost_type === 'indirect'). Used to split `rows` into
// Direct/Indirect subsets before building either hierarchy — Direct and
// Indirect are never mixed into the same classifier or the same tree.
export function isIndirectCostCode(costCode, { codeToRow }) {
  if (!costCode) return false
  const row = codeToRow[costCode.trim().toLowerCase()]
  return row?.cost_type === 'indirect'
}

// ── internal — not exported ──────────────────────────────────────────
// Walk up from a Sub Code to its resolved top-level Cost Code, then read
// that Cost Code's own indirect_category_id (never a Sub Code's — the
// category is a Level-1 attribute, exactly as Cost Control's
// buildCumulativeData() reads it: `parents = allCodes.filter(c => c.level
// === 1)`, then `indirect_category_id` off each parent). Returns
// 'Uncategorized' — the exact same label CostControl.jsx displays for
// Indirect Cost Codes with no category assigned — if the Cost Code has no
// indirect_category_id, or if that id doesn't match any row in
// indirectCategoryMap (e.g. the category was deleted).
function resolveIndirectCategoryForCostCode(costCode, { codeToRow, indirectCategoryMap }) {
  const UNCATEGORIZED = 'Uncategorized'
  if (!costCode) return UNCATEGORIZED
  const row = codeToRow[costCode.trim().toLowerCase()]
  if (!row || !row.indirect_category_id) return UNCATEGORIZED
  return indirectCategoryMap[row.indirect_category_id] || UNCATEGORIZED
}

// ── exported ──────────────────────────────────────────────────────────
// The classifier for Indirect rows, passed into buildHierarchyFromRows()
// exactly like resolveResourceType/resolveResourceDictCode are for
// Direct rows. Unlike those two (which classify by walking up from the
// Sub Code through resource_dictionary), this classifies by the row's
// RESOLVED TOP-LEVEL COST CODE's own indirect_category_id — because
// indirect_category_id is a Cost-Code-level attribute, not a
// Sub-Code-level one. `code` here is expected to be the Sub Code (the
// same argument shape as resolveResourceType/resolveResourceDictCode, so
// buildHierarchyFromRows() can call any of the three interchangeably) —
// this function resolves it up to Level 1 first via resolveToCostCode().
export function resolveIndirectCategory(code, lookups) {
  const resolvedCostCode = resolveToCostCode(code, lookups)
  return resolveIndirectCategoryForCostCode(resolvedCostCode, lookups)
}

/**
 * buildHierarchyFromRows — the ONLY hierarchy-building function in the
 * codebase. See module doc-comment above for the full architecture
 * contract and the Single Builder Invariant.
 *
 * The first-level grouping key is fully determined by `classifyFn` — the
 * builder itself has no concept of "Resource Type" vs "Resource Code" vs
 * any other grouping; it only groups rows by whatever string classifyFn
 * returns for each row's Sub Code, then sums. The output shape is
 * IDENTICAL regardless of which classifier is passed in — only the
 * first-level keys (and which rows fall under which key) differ. This is
 * what keeps "By Source Type" and "By Resource Code" (and any future
 * grouping — Vendor, Discipline, Package, ...) a single implementation
 * rather than one hierarchy-building function per mode.
 *
 * @param {Array} rows - any subset of buildMonthlyActualCostData's own
 *   `rows` output (full dataset or a single period's rows — the function
 *   itself has no concept of "full" vs "period-scoped", it just groups
 *   and sums whatever it's given)
 * @param {string[]} periods - the period keys present in `rows`
 * @param {string|null} lastPeriod
 * @param {string|null} prevPeriod
 * @param {{codeToRow: object, idToRow: object, dictTypeMap: object}} lookups
 * @param {(subCode: string, lookups: object) => string} [classifyFn] -
 *   the first-level grouping classifier. Defaults to resolveResourceType
 *   (Resource Type / "By Source Type" grouping) so every existing call
 *   site keeps working unchanged. Pass resolveResourceDictCode (exported
 *   below) for the "By Resource Code" grouping instead.
 * @returns {Record<string, {
 *   periodTotals: Record<string, number>, total: number, movement: number,
 *   costCodes: Record<string, {
 *     desc: string, periodTotals: Record<string, number>, total: number, movement: number,
 *     subCodes: Record<string, {
 *       desc: string, source: string, periodTotals: Record<string, number>, total: number, movement: number,
 *     }>
 *   }>
 * }>}
 */
export function buildHierarchyFromRows(rows, periods, lastPeriod, prevPeriod, lookups, classifyFn = resolveResourceType) {
  const movementOf = (periodTotals) =>
    lastPeriod ? (periodTotals[lastPeriod] || 0) - (prevPeriod ? periodTotals[prevPeriod] || 0 : 0) : 0

  const sumPeriods = (list) => {
    const periodTotals = {}
    periods.forEach(p => { periodTotals[p] = list.reduce((s, r) => s + (r.periods[p] || 0), 0) })
    const total = Object.values(periodTotals).reduce((s, v) => s + v, 0)
    return { periodTotals, total, movement: movementOf(periodTotals) }
  }

  // Level 3 grouping: <classifyFn result> -> costCode -> subCode -> [rows]
  const tree = {}
  for (const r of rows) {
    if (!r.subCode || r.subCode === '—') continue
    const costCode = realCodeFor(r, lookups)
    if (!costCode || costCode === '—') continue
    const groupKey = classifyFn(r.subCode, lookups) || 'Unclassified'

    if (!tree[groupKey]) tree[groupKey] = {}
    if (!tree[groupKey][costCode]) {
      // The Cost Code node's own description must come from cost_codes
      // itself (via lookups.codeToRow), not from r.costCodeDesc. In
      // `rows`, costCodeDesc is populated per-source to mean "the Sub
      // Code's own description" (see monthlyActualCost.js's addAmt calls
      // for Store/IPC/Petty Cash) — correct for the Sub Code row below,
      // but wrong here: using it for the parent Cost Code row would
      // borrow whichever Sub Code's row happened to be grouped first,
      // the same class of bug fixed for the placeholder IPC/Petty Cash
      // rows in MonthlyActualCost.jsx (Phase A). Falls back to '' (never
      // r.costCodeDesc) if the Cost Code itself isn't found in lookups,
      // so a missing reference row never silently borrows a Sub Code's
      // description either.
      const costCodeRow = lookups.codeToRow[costCode.trim().toLowerCase()]
      tree[groupKey][costCode] = { desc: costCodeRow?.description || '', subCodes: {} }
    }
    if (!tree[groupKey][costCode].subCodes[r.subCode]) {
      tree[groupKey][costCode].subCodes[r.subCode] = { desc: r.costCodeDesc, source: r.source, rows: [] }
    }
    tree[groupKey][costCode].subCodes[r.subCode].rows.push(r)
  }

  // Fold the grouped rows into periodTotals/total/movement at each level,
  // finest grain (Sub Code) first, then sum upward — never recomputed
  // independently at a coarser level.
  const hierarchy = {}
  for (const [groupKey, costCodes] of Object.entries(tree)) {
    hierarchy[groupKey] = { costCodes: {} }
    let groupRowsAll = []

    for (const [costCode, { desc, subCodes }] of Object.entries(costCodes)) {
      hierarchy[groupKey].costCodes[costCode] = { desc, subCodes: {} }
      let ccRowsAll = []

      for (const [subCode, { desc: subDesc, source, rows: subRows }] of Object.entries(subCodes)) {
        const { periodTotals, total, movement } = sumPeriods(subRows)
        hierarchy[groupKey].costCodes[costCode].subCodes[subCode] = { desc: subDesc, source, periodTotals, total, movement }
        ccRowsAll = ccRowsAll.concat(subRows)
      }

      const ccAgg = sumPeriods(ccRowsAll)
      hierarchy[groupKey].costCodes[costCode] = { desc, ...ccAgg, subCodes: hierarchy[groupKey].costCodes[costCode].subCodes }
      groupRowsAll = groupRowsAll.concat(ccRowsAll)
    }

    const groupAgg = sumPeriods(groupRowsAll)
    hierarchy[groupKey] = { ...groupAgg, costCodes: hierarchy[groupKey].costCodes }
  }

  return hierarchy
}

/**
 * buildHierarchyLookups — exported so monthlyActualCost.js (the data
 * layer, which owns ccData/dictData/indirectCategoriesData) can build the
 * lookups it passes into buildHierarchyFromRows(), without duplicating
 * the buildLookups() implementation. Not intended for use by React
 * components — reference data and lookup structures stay internal to the
 * data layer, per the agreed contract (report data is public, reference
 * data is not).
 *
 * @param {Array} ccData - raw cost_codes rows (includes cost_type,
 *   indirect_category_id — the same reference columns Cost Control reads)
 * @param {Array} dictData - raw resource_dictionary rows
 * @param {Array} indirectCategoriesData - raw indirect_categories rows
 * @returns {{codeToRow: object, idToRow: object, dictTypeMap: object, indirectCategoryMap: object}}
 */
export function buildHierarchyLookups(ccData, dictData, indirectCategoriesData) {
  return buildLookups(ccData, dictData, indirectCategoriesData)
}
